import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Alert,
  AlertIcon,
  SimpleGrid,
  Icon,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { Brain, Code, Lightbulb, ChevronRight, Rocket, Star } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

const MotionBox = motion(Box);

const JobCard = ({
  title,
  description,
  responsibilities,
}: {
  title: string;
  description: string;
  responsibilities: string[];
}) => {
  const { trackEvent } = useAnalytics();

  return (
    <Box
      p={{ base: 4, md: 6 }}
      borderRadius='xl'
      bg='rgba(11, 61, 145, 0.3)'
      border='1px solid'
      borderColor='brand.accent'
      transition='all 0.3s'
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '0 4px 20px rgba(70, 255, 255, 0.2)',
      }}
      role='article'
      onMouseEnter={() =>
        trackEvent('job_card_viewed', {
          job_title: title,
        })
      }>
      <Heading as='h3' size={{ base: 'sm', md: 'md' }} color='brand.light' mb={4}>
        {title}
      </Heading>
      <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }} mb={4}>
        {description}
      </Text>
      <List spacing={2}>
        {responsibilities.map((resp, index) => (
          <ListItem
            key={index}
            color='gray.300'
            display='flex'
            alignItems='center'
            fontSize={{ base: 'sm', md: 'md' }}>
            <ListIcon as={ChevronRight} color='brand.accent' aria-hidden='true' />
            {resp}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const VisionCultureSection = () => {
  const { trackEvent } = useAnalytics();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleLearnMore = () => {
    trackEvent('vision_cta_clicked', {
      section: 'careers',
      action: 'learn_more',
    });
    const element = document.getElementById('about');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <MotionBox
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8 }}
      p={{ base: 4, md: 8 }}
      borderRadius='xl'
      bg='rgba(11, 61, 145, 0.3)'
      border='1px solid'
      borderColor='brand.accent'
      position='relative'
      overflow='hidden'
      className='cosmic-card'>
      <VStack spacing={{ base: 4, md: 6 }} align='stretch'>
        <Heading as='h3' size={{ base: 'md', md: 'lg' }} color='brand.light' textAlign='center'>
          Our Vision & Culture
        </Heading>
        <Text color='gray.300' fontSize={{ base: 'sm', md: 'lg' }} textAlign='center'>
          At GallifreyAI, we're building more than just tools—we're creating a future where
          neurodiversity drives innovation. Founded by a neurodivergent innovator, we understand
          that different perspectives are the key to solving complex challenges in unique ways.
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 4, md: 6 }}>
          <Box>
            <Icon
              as={Brain}
              color='brand.accent'
              boxSize={{ base: 6, md: 8 }}
              mb={4}
              aria-hidden='true'
            />
            <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
              Neurodiversity First
            </Text>
            <Text color='gray.300' fontSize={{ base: 'xs', md: 'sm' }}>
              Embracing different ways of thinking as our greatest strength in innovation
            </Text>
          </Box>
          <Box>
            <Icon
              as={Code}
              color='brand.accent'
              boxSize={{ base: 6, md: 8 }}
              mb={4}
              aria-hidden='true'
            />
            <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
              Adaptive Workflows
            </Text>
            <Text color='gray.300' fontSize={{ base: 'xs', md: 'sm' }}>
              Creating tools that adapt to your natural work patterns
            </Text>
          </Box>
          <Box>
            <Icon
              as={Lightbulb}
              color='brand.accent'
              boxSize={{ base: 6, md: 8 }}
              mb={4}
              aria-hidden='true'
            />
            <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
              Innovation Through Diversity
            </Text>
            <Text color='gray.300' fontSize={{ base: 'xs', md: 'sm' }}>
              Leveraging unique perspectives to build revolutionary solutions
            </Text>
          </Box>
          <Box>
            <Icon
              as={Star}
              color='brand.accent'
              boxSize={{ base: 6, md: 8 }}
              mb={4}
              aria-hidden='true'
            />
            <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
              Future-Focused
            </Text>
            <Text color='gray.300' fontSize={{ base: 'xs', md: 'sm' }}>
              Constantly pushing boundaries to shape tomorrow's development landscape
            </Text>
          </Box>
        </SimpleGrid>
        <Box textAlign='center' mt={{ base: 2, md: 4 }}>
          <Button
            variant='primary'
            size={{ base: 'md', md: 'lg' }}
            rightIcon={<ChevronRight />}
            onClick={handleLearnMore}
            width={{ base: 'full', md: 'auto' }}>
            Learn More About Our Mission
          </Button>
        </Box>
      </VStack>
    </MotionBox>
  );
};

const Careers = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    resume: null as File | null,
  });

  const toast = useToast();
  const { trackEvent } = useAnalytics();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({ ...formData, resume: file });
      trackEvent('resume_uploaded', {
        file_size: file.size,
        file_type: file.type,
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    trackEvent('career_application_submitted', {
      has_resume: !!formData.resume,
      message_length: formData.message.length,
      resume_size: formData.resume?.size,
      resume_type: formData.resume?.type,
    });

    toast({
      title: 'Application received!',
      description: "We'll review your application and get back to you soon.",
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    setFormData({ name: '', email: '', message: '', resume: null });
  };

  const jobs = [
    {
      title: 'AI Engineer',
      description: 'Shape the future of AI-powered development tools',
      responsibilities: [
        'Design and implement AI models for code analysis',
        'Develop machine learning pipelines',
        'Optimize AI performance and accuracy',
      ],
    },
    {
      title: 'Frontend Developer',
      description: 'Create intuitive interfaces for complex development tools',
      responsibilities: [
        'Build responsive web applications',
        'Implement real-time features',
        'Optimize user experience',
      ],
    },
    {
      title: 'Product Manager',
      description: 'Guide the evolution of our revolutionary developer tools',
      responsibilities: [
        'Define product strategy and roadmap',
        'Gather and analyze user feedback',
        'Coordinate with development teams',
      ],
    },
  ];

  return (
    <Box
      id='careers'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgGradient='linear(to-b, rgba(11, 61, 145, 0.2), brand.dark)'
      bgImage="linear-gradient(to bottom, rgba(2, 8, 20, 0.95), rgba(2, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80')"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      bgSize='cover'
      bgPosition='center'
      onMouseEnter={() => trackEvent('section_viewed', { section: 'careers' })}>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <MotionBox
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          <VStack spacing={{ base: 8, md: 12 }} align='stretch'>
            <VStack spacing={{ base: 3, md: 4 }} align='stretch'>
              <Heading
                as='h2'
                size={{ base: 'lg', md: 'xl' }}
                color='brand.light'
                letterSpacing='wider'
                textAlign='center'>
                Join Our Journey
              </Heading>
              <Text color='gray.300' fontSize={{ base: 'md', md: 'lg' }} textAlign='center'>
                We're building the future of development, and we want you to be part of it.
              </Text>
            </VStack>

            <Alert
              status='info'
              variant='solid'
              bg='rgba(11, 61, 145, 0.6)'
              borderRadius='xl'
              borderWidth='1px'
              borderColor='brand.accent'
              flexDirection={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'flex-start', md: 'center' }}
              p={{ base: 4, md: 6 }}>
              <AlertIcon />
              <Box>
                <Text fontWeight='bold' mb={2} fontSize={{ base: 'sm', md: 'md' }}>
                  Pioneering the Future of Development 🚀
                </Text>
                <Text fontSize={{ base: 'xs', md: 'sm' }}>
                  GallifreyAI was founded by an innovator who brings unique perspectives and
                  problem-solving approaches to revolutionize development tools. Our neurodiversity
                  drives innovation, enabling us to see challenges differently and create
                  groundbreaking solutions.
                </Text>
              </Box>
            </Alert>

            <VisionCultureSection />

            <Box>
              <Heading
                as='h3'
                size={{ base: 'md', md: 'lg' }}
                color='brand.light'
                mb={{ base: 6, md: 8 }}
                textAlign='center'>
                Open Positions
              </Heading>
              <SimpleGrid
                columns={{ base: 1, lg: 3 }}
                spacing={{ base: 4, md: 6 }}
                mb={{ base: 6, md: 8 }}>
                {jobs.map((job, index) => (
                  <JobCard key={index} {...job} />
                ))}
              </SimpleGrid>
              <Box
                p={{ base: 3, md: 4 }}
                bg='rgba(11, 61, 145, 0.3)'
                borderRadius='xl'
                borderWidth='1px'
                borderColor='brand.accent'
                mb={{ base: 6, md: 8 }}>
                <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
                  <Icon
                    as={Rocket}
                    color='brand.accent'
                    boxSize={{ base: 4, md: 5 }}
                    mr={2}
                    display='inline'
                    aria-hidden='true'
                  />
                  <Text as='span' fontWeight='bold'>
                    Early Stage Opportunity:{' '}
                  </Text>
                  Join us at the ground level and help shape not just your role, but the entire
                  future of development tools.
                </Text>
              </Box>
            </Box>

            <Box
              as='form'
              onSubmit={handleSubmit}
              p={{ base: 4, md: 8 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'
              role='form'
              aria-label='Job Application Form'>
              <VStack spacing={{ base: 3, md: 4 }}>
                <Text color='gray.300' mb={{ base: 2, md: 4 }} fontSize={{ base: 'sm', md: 'md' }}>
                  We're committed to providing an inclusive environment where all minds can thrive.
                  Let us know if you need any accommodations during the interview process.
                </Text>
                <FormControl isRequired>
                  <FormLabel color='brand.light' fontSize={{ base: 'sm', md: 'md' }}>
                    Name
                  </FormLabel>
                  <Input
                    type='text'
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    aria-label='Full name'
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light' fontSize={{ base: 'sm', md: 'md' }}>
                    Email
                  </FormLabel>
                  <Input
                    type='email'
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    aria-label='Email address'
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light' fontSize={{ base: 'sm', md: 'md' }}>
                    Cover Letter
                  </FormLabel>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    rows={5}
                    placeholder="Tell us about yourself, your vision, and how you'd like to contribute to our mission..."
                    aria-label='Cover letter'
                    fontSize={{ base: 'sm', md: 'md' }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color='brand.light' fontSize={{ base: 'sm', md: 'md' }}>
                    Resume
                  </FormLabel>
                  <Input
                    type='file'
                    accept='.pdf,.doc,.docx'
                    onChange={handleFileChange}
                    bg='rgba(255, 255, 255, 0.1)'
                    border='1px solid'
                    borderColor='brand.accent'
                    _hover={{ borderColor: 'brand.accent' }}
                    _focus={{ borderColor: 'brand.accent', boxShadow: '0 0 0 1px #46ffff' }}
                    p={1}
                    aria-label='Upload resume'
                    fontSize={{ base: 'sm', md: 'md' }}
                    height={{ base: '40px', md: 'auto' }}
                  />
                  <Text fontSize={{ base: 'xs', md: 'sm' }} color='gray.400' mt={1}>
                    Accepted formats: PDF, DOC, DOCX
                  </Text>
                </FormControl>
                <Button
                  type='submit'
                  variant='primary'
                  size={{ base: 'md', md: 'lg' }}
                  width='full'
                  mt={{ base: 2, md: 4 }}
                  fontSize={{ base: 'sm', md: 'md' }}
                  height={{ base: '40px', md: 'auto' }}
                  aria-label='Submit application'>
                  Submit Application
                </Button>
              </VStack>
            </Box>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Careers;
